import { Beer, Dog, Home, Waves } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { RankingBadge } from '@/components/dashboard/RankingBadge';
import { RecencyBadge } from '@/components/dashboard/RecencyBadge';
import { IncompleteDataBadge } from '@/components/dashboard/IncompleteDataBadge';
import type {
  ListingDetails,
  NeighborsInRadius,
  Property,
  PropertyRankingDTO,
} from '@/types/db';
import { cn } from '@/lib/utils';

export interface PropertyHeaderProps {
  property: Property & { is_incomplete: boolean };
  ranking: PropertyRankingDTO;
  neighborsInRadius: NeighborsInRadius;
  listingDetails: ListingDetails | null;
  /** Days since listed (created_at). Drives `<RecencyBadge>`. */
  daysSinceListed: number;
  className?: string;
}

type StatusTone = 'active' | 'closed' | 'sold';

/**
 * Conservative status derivation: the EJS data model doesn't track sold/
 * closed signals yet; default to "Active" unless a future field
 * (`listing_details.status` or a /sold/ URL marker) explicitly surfaces.
 * Keeping this in code (not config) so a future Plan can extend without
 * touching the component's external contract.
 */
function deriveStatus(listingDetails: ListingDetails | null): StatusTone {
  if (!listingDetails) return 'active';
  const url = listingDetails.zillow_url ?? '';
  if (url.includes('/sold/')) return 'sold';
  if (url.includes('/closed/')) return 'closed';
  return 'active';
}

const STATUS_LABELS: Record<StatusTone, string> = {
  active: 'Active',
  closed: 'Closed',
  sold: 'Sold',
};

const STATUS_CLASSES: Record<StatusTone, string> = {
  active: 'bg-moss-500/20 text-moss-500 border border-moss-500/40',
  closed: 'bg-foam-50/10 text-foam-50/60 border border-foam-50/20',
  sold: 'bg-coral-500/20 text-coral-100 border border-coral-500/40',
};

/**
 * Map of category-key → display config. Rendered in fixed order; zero counts
 * are omitted entirely (UI-SPEC § 3.2).
 */
const NEIGHBOR_DISPLAY: Array<{
  key: keyof NeighborsInRadius;
  icon: LucideIcon;
  singular: string;
  plural: string;
}> = [
  { key: 'dog_park', icon: Dog, singular: 'dog park', plural: 'dog parks' },
  { key: 'dog_beach', icon: Waves, singular: 'dog beach', plural: 'dog beaches' },
  { key: 'daycare', icon: Home, singular: 'daycare', plural: 'daycares' },
  { key: 'dog_bar', icon: Beer, singular: 'dog bar', plural: 'dog bars' },
];

/**
 * `<PropertyHeader>` — top glass-strong band on the property detail page.
 * Per UI-SPEC § 3.2.
 *
 * Anatomy:
 * - Outer: `<GlassPanel as="header" variant="strong">` two-column at md+,
 *   single-column at <md.
 * - Left: address `<h1>` + meta row (RecencyBadge · status pill · subtitle)
 *   + IncompleteDataBadge (when applicable) + neighbors-in-radius pill row.
 * - Right: `<RankingBadge>` (96×96 from Phase 05).
 *
 * Status pill: derived from `listingDetails`. Defaults to "Active" — see
 * `deriveStatus` for the conservative mapping.
 *
 * Singular/plural: `1 dog park` vs `3 dog parks`. Zero counts omitted.
 *
 * ARIA: `<h1>` is the page's primary landmark heading. RankingBadge owns
 * its own tooltip ARIA from Phase 05.
 */
export function PropertyHeader({
  property,
  ranking,
  neighborsInRadius,
  listingDetails,
  daysSinceListed,
  className,
}: PropertyHeaderProps) {
  const status = deriveStatus(listingDetails);
  const subtitleParts: string[] = [];
  if (property.county) {
    subtitleParts.push(
      `${property.county.charAt(0).toUpperCase()}${property.county.slice(1)}`,
    );
  }
  if (property.city) {
    subtitleParts.push(property.city);
  }
  const subtitle = subtitleParts.join(' · ');

  const visibleNeighbors = NEIGHBOR_DISPLAY.flatMap(
    ({ key, icon: Icon, singular, plural }) => {
      const count = neighborsInRadius[key];
      if (!count || count <= 0) return [];
      const label = count === 1 ? singular : plural;
      return [
        <span
          key={key}
          className="inline-flex items-center gap-1 bg-ocean-800/60 border border-foam-50/10 rounded-full px-2.5 h-7 text-sm text-foam-50"
        >
          <Icon aria-hidden className="w-3.5 h-3.5 text-foam-50/70" />
          <span className="tabular-nums font-semibold">{count}</span>
          <span className="text-foam-50/80">{label}</span>
        </span>,
      ];
    },
  );

  return (
    <GlassPanel
      as="header"
      variant="strong"
      className={cn(
        'grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 md:gap-6 p-4 md:p-6 items-start md:items-center',
        className,
      )}
    >
      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-semibold text-foam-50 leading-tight tracking-tight">
          {property.address || `Property #${property.id}`}
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <RecencyBadge daysSince={daysSinceListed} />
          <span
            role="status"
            aria-label={`Listing status: ${STATUS_LABELS[status]}`}
            className={cn(
              'inline-flex h-6 px-2 items-center rounded-full text-xs font-semibold whitespace-nowrap',
              STATUS_CLASSES[status],
            )}
          >
            {STATUS_LABELS[status]}
          </span>
          {subtitle && (
            <span className="text-sm text-foam-50/70">{subtitle}</span>
          )}
        </div>
        {property.is_incomplete && (
          <div>
            <IncompleteDataBadge />
          </div>
        )}
        {visibleNeighbors.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1">{visibleNeighbors}</div>
        )}
      </div>
      <div className="md:justify-self-end">
        <RankingBadge
          score={ranking.composite}
          isIncomplete={ranking.is_incomplete}
          dogProximityFactor={ranking.dog_proximity_factor}
        />
      </div>
    </GlassPanel>
  );
}
